import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker, Modal } from 'antd'
import moment, { Moment } from 'moment'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/pt'
import esES from 'antd/es/date-picker/locale/es_ES'
import enUS from 'antd/es/date-picker/locale/en_US'
import frFR from 'antd/es/date-picker/locale/fr_FR'
import ptBR from 'antd/es/date-picker/locale/pt_BR'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation'
import { Trans, useTranslation } from 'react-i18next'
import {
  enqueueDc4Report,
  previewDc4Report,
} from 'api/administration/dc4Reports'
import { useNotification } from 'hooks/notifications/useNotification'
import {
  disabledFromDate,
  disabledToDate,
  isToOutsideRange,
  rangeDayClassName,
  startOfToday,
  toDay,
} from './dc4ReportPeriod'
import {
  BannerContent,
  BannerIcon,
  BannerText,
  Description,
  EmptyColumnsBanner,
  EmptyColumnsIntro,
  EmptyColumnsList,
  FieldLabel,
  MaxMonthsHint,
  ModalWrap,
  MoreDetailsButton,
  PeriodField,
  PeriodFields,
  PeriodSection,
  PeriodTitle,
  PreviewBanner,
  PreviewBannerIcon,
  PreviewBannerText,
} from './styled'

const EMPTY_COLUMN_KEYS = [
  'estado',
  'municipio',
  'nivEstudios',
  'docProbatorio',
  'institucion',
  'curso',
  'establecimiento',
] as const

interface Props {
  open: boolean
  onClose: () => void
}

const Dc4ReportGenerateModal: React.FC<Props> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation()
  const notification = useNotification()
  const prefix = 'administration.certificate.dc4ReportModal'
  const datePickerLocale = useMemo(() => {
    switch (i18n.language) {
      case 'es':
        return esES
      case 'fr':
        return frFR
      case 'pt':
        return ptBR
      default:
        return enUS
    }
  }, [i18n.language])

  useEffect(() => {
    moment.locale(i18n.language === 'pt' ? 'pt' : i18n.language || 'es')
  }, [i18n.language])

  const [from, setFrom] = useState<Moment | null>(null)
  const [to, setTo] = useState<Moment | null>(() => startOfToday())
  const [count, setCount] = useState<number | null>(null)
  const [canGenerate, setCanGenerate] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const reset = useCallback(() => {
    setFrom(null)
    setTo(startOfToday())
    setCount(null)
    setCanGenerate(false)
    setDetailsOpen(false)
  }, [])

  useEffect(() => {
    if (open) reset()
  }, [open, reset])

  const handleFromChange = (value: Moment | null) => {
    const nextFrom = value ? toDay(value) : null
    setFrom(nextFrom)
    if (nextFrom && to && isToOutsideRange(nextFrom, to)) {
      setTo(null)
      setCount(null)
      setCanGenerate(false)
    }
  }

  const handleToChange = (value: Moment | null) => {
    setTo(value ? toDay(value) : null)
  }

  const runPreview = useCallback(async () => {
    if (!from || !to) {
      setCount(null)
      setCanGenerate(false)
      return
    }

    setLoadingPreview(true)
    try {
      const result = await previewDc4Report(
        from.format('YYYY-MM-DD'),
        to.format('YYYY-MM-DD')
      )
      setCount(result.count)
      setCanGenerate(result.canGenerate)
    } catch {
      setCount(null)
      setCanGenerate(false)
      notification.error(t(`${prefix}.errors.preview`))
    } finally {
      setLoadingPreview(false)
    }
  }, [from, to, notification, prefix, t])

  useEffect(() => {
    if (!open) return
    const handle = window.setTimeout(() => {
      runPreview()
    }, 300)
    return () => window.clearTimeout(handle)
  }, [open, runPreview])

  const handleGenerate = async () => {
    if (!from || !to || !canGenerate) return
    setSubmitting(true)
    try {
      await enqueueDc4Report(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'))
      notification.success(t(`${prefix}.success`))
      onClose()
    } catch {
      notification.error(t(`${prefix}.errors.generate`))
    } finally {
      setSubmitting(false)
    }
  }

  const renderRangeDate = (current: Moment) => (
    <div
      className={`ant-calendar-date ${rangeDayClassName(
        current,
        from,
        to
      )}`.trim()}
    >
      {current.date()}
    </div>
  )

  const getCalendarContainer = () => wrapRef.current || document.body

  const previewState: 'idle' | 'loading' | 'empty' | 'ready' = (() => {
    if (!from || !to) return 'idle'
    if (loadingPreview) return 'loading'
    if (count === null) return 'idle'
    if (count === 0) return 'empty'
    return 'ready'
  })()
  const previewVariant = previewState === 'empty' ? 'warning' : 'info'

  return (
    <>
      <ModalWrap ref={wrapRef}>
        <Modal
          visible={open}
          title={t(`${prefix}.title`)}
          onCancel={onClose}
          onOk={handleGenerate}
          okText={t(`${prefix}.generate`)}
          okButtonProps={{
            disabled:
              !canGenerate || submitting || loadingPreview || count === 0,
            loading: submitting,
          }}
          cancelText={t(`${prefix}.cancel`)}
          destroyOnClose
          okType="primary"
          width={640}
          wrapClassName="dc4-report-modal"
          getContainer={false}
        >
          <Description>{t(`${prefix}.description`)}</Description>

          <EmptyColumnsBanner>
            <BannerIcon>
              <FontAwesomeIcon icon={faInfoCircle} />
            </BannerIcon>
            <BannerContent>
              <BannerText>{t(`${prefix}.emptyColumnsBanner`)}</BannerText>
              <MoreDetailsButton
                type="button"
                onClick={() => setDetailsOpen(true)}
                data-testid="dc4-report-more-details"
              >
                {t(`${prefix}.moreDetails`)}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </MoreDetailsButton>
            </BannerContent>
          </EmptyColumnsBanner>

          <PeriodSection>
            <PeriodTitle>{t(`${prefix}.periodTitle`)}</PeriodTitle>
            <PeriodFields>
              <PeriodField>
                <FieldLabel htmlFor="dc4-report-from">
                  {t(`${prefix}.from`)}
                </FieldLabel>
                <DatePicker
                  id="dc4-report-from"
                  value={from}
                  locale={datePickerLocale}
                  onChange={handleFromChange}
                  disabledDate={disabledFromDate}
                  dateRender={renderRangeDate}
                  getCalendarContainer={getCalendarContainer}
                  placeholder={t(`${prefix}.selectDate`)}
                  format="DD/MM/YYYY"
                  showToday={false}
                  data-testid="dc4-report-from"
                />
                <MaxMonthsHint>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  {t(`${prefix}.maxMonthsHint`)}
                </MaxMonthsHint>
              </PeriodField>
              <PeriodField>
                <FieldLabel htmlFor="dc4-report-to">
                  {t(`${prefix}.to`)}
                </FieldLabel>
                <DatePicker
                  id="dc4-report-to"
                  value={to}
                  locale={datePickerLocale}
                  onChange={handleToChange}
                  disabledDate={(current) => disabledToDate(current, from)}
                  dateRender={renderRangeDate}
                  getCalendarContainer={getCalendarContainer}
                  placeholder={t(`${prefix}.selectDate`)}
                  format="DD/MM/YYYY"
                  allowClear={false}
                  data-testid="dc4-report-to"
                />
              </PeriodField>
            </PeriodFields>
          </PeriodSection>

          <PreviewBanner $variant={previewVariant}>
            <PreviewBannerIcon $variant={previewVariant}>
              {previewState === 'loading' ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : previewState === 'empty' ? (
                <FontAwesomeIcon icon={faTriangleExclamation} />
              ) : (
                <FontAwesomeIcon icon={faInfoCircle} />
              )}
            </PreviewBannerIcon>
            <PreviewBannerText
              $variant={previewVariant}
              data-testid="dc4-report-preview-count"
            >
              {previewState === 'idle' && t(`${prefix}.selectPeriodHint`)}
              {previewState === 'loading' && t(`${prefix}.loadingPreview`)}
              {previewState === 'empty' && t(`${prefix}.emptyPeriod`)}
              {previewState === 'ready' && (
                <Trans
                  i18nKey={`${prefix}.previewCount`}
                  values={{ count }}
                  components={{ strong: <strong /> }}
                />
              )}
            </PreviewBannerText>
          </PreviewBanner>
        </Modal>
      </ModalWrap>

      <ModalWrap>
        <Modal
          visible={detailsOpen}
          title={t(`${prefix}.moreDetailsTitle`)}
          onCancel={() => setDetailsOpen(false)}
          footer={null}
          destroyOnClose
          width={480}
          wrapClassName="dc4-report-modal"
          getContainer={false}
        >
          <EmptyColumnsIntro>
            {t(`${prefix}.moreDetailsIntro`)}
          </EmptyColumnsIntro>
          <EmptyColumnsList>
            {EMPTY_COLUMN_KEYS.map((key) => (
              <li key={key}>{t(`${prefix}.emptyColumns.${key}`)}</li>
            ))}
          </EmptyColumnsList>
        </Modal>
      </ModalWrap>
    </>
  )
}

export default Dc4ReportGenerateModal
